"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Authentication = void 0;
const react_native_config_1 = __importDefault(require("react-native-config"));
const react_native_1 = require("react-native");
const app_1 = require("firebase/app");
const auth_1 = require("firebase/auth");
const FirebaseAnalytics_1 = __importDefault(require("./FirebaseAnalytics"));
const Chat_1 = __importDefault(require("../components/Chat"));
const async_storage_1 = __importDefault(require("@react-native-async-storage/async-storage"));
const NetworkContext_1 = require("../contexts/NetworkContext");
class Authentication {
    App;
    Auth;
    suppressBiometricAutoPromptOnce = false;
    constructor() {
        this.App = (0, app_1.initializeApp)({
            apiKey: react_native_config_1.default.FIREBASE_KEY,
            authDomain: react_native_config_1.default.FIREBASE_URL,
            projectId: react_native_config_1.default.GCP_PROJECT,
            storageBucket: react_native_config_1.default.FIREBASE_STORAGE_BUCKET,
            messagingSenderId: react_native_config_1.default.FIREBASE_MESSAGING_SENDER_ID,
            appId: react_native_config_1.default.FIREBASE_APP_ID,
            measurementId: react_native_config_1.default.FIREBASE_ANALYTICS_ID
        });
        this.Auth = (0, auth_1.getAuth)();
    }
    OnAuthStateChanged(handler) {
        return this.Auth.onAuthStateChanged(handler);
    }
    OnIdTokenChanged(handler) {
        return this.Auth.onIdTokenChanged(handler);
    }
    GetUser() {
        return new Promise((resolve, reject) => {
            resolve(this.Auth.currentUser);
        });
    }
    GetUserWait() {
        return new Promise((resolve, reject) => {
            var wait = 0;
            (function waitForUser(instance) {
                if (instance.Auth.currentUser) {
                    return resolve(instance.Auth.currentUser);
                }
                else if (wait >= 2000) {
                    return reject();
                }
                wait = wait + 10;
                setTimeout(waitForUser, 10, instance);
            })(this);
        });
    }
    GetToken() {
        return this.GetUserWait().then((u) => u?.getIdToken());
    }
    GetRedirectResult() {
        return (0, auth_1.getRedirectResult)(this.Auth);
    }
    async SignIn(username, password) {
        FirebaseAnalytics_1.default.LogEvent('login', { 'method': 'username_password' });
        try {
            return await (0, auth_1.signInWithEmailAndPassword)(this.Auth, username?.toLowerCase(), password);
        }
        catch (error) {
            if (error?.message?.includes('Network request failed') ||
                error?.message?.includes('failed to fetch')) {
                NetworkContext_1.NetworkErrorSetter.trigger(true);
            }
            throw error;
        }
    }
    async SignInWithToken(token, method) {
        FirebaseAnalytics_1.default.LogEvent('login', { 'method': method ?? 'token' });
        try {
            (0, auth_1.signInWithCustomToken)(this.Auth, token).then((c) => null);
        }
        catch (error) {
            if (error?.message?.includes('Network request failed') ||
                error?.message?.includes('failed to fetch')) {
                NetworkContext_1.NetworkErrorSetter.trigger(true);
            }
            throw error;
        }
    }
    async SignInWithLink(email, link) {
        FirebaseAnalytics_1.default.LogEvent('login', { 'method': 'link' });
        return (0, auth_1.signInWithEmailLink)(this.Auth, email?.toLowerCase(), link).then((c) => null);
    }
    async SignInWithGoogle(idToken, accessToken) {
        let credential = auth_1.GoogleAuthProvider.credential(idToken, accessToken);
        FirebaseAnalytics_1.default.LogEvent('login', { 'method': 'google' });
        return (0, auth_1.signInWithCredential)(this.Auth, credential).then((c) => null);
    }
    async SignInWithApple(appleCredential) {
        FirebaseAnalytics_1.default.LogEvent('login', { 'method': 'apple' });
        return (0, auth_1.signInWithCredential)(this.Auth, appleCredential).then((c) => null);
    }
    async RevokeWithApple(appleToken, currentProvider, token) {
        FirebaseAnalytics_1.default.LogEvent('revoke', { 'method': 'apple' });
        const currentUser = this.Auth.currentUser;
        return (0, auth_1.unlink)(currentUser, "apple.com").then(() => {
            return (0, auth_1.revokeAccessToken)(this.Auth, appleToken).catch(() => { }).finally(() => {
                if (currentProvider == "apple.com")
                    return this.SignOut();
            });
        });
    }
    async RevokeWithGoogle(currentProvider) {
        FirebaseAnalytics_1.default.LogEvent('revoke', { 'method': 'google' });
        const currentUser = this.Auth.currentUser;
        return (0, auth_1.unlink)(currentUser, "google.com").then(() => {
            if (currentProvider == "google.com")
                return this.SignOut();
        });
    }
    async LinkWithApple(appleToken) {
        return new Promise(() => { });
    }
    async LinkWithGoogle(googleCredential) {
        return new Promise((resolve, reject) => {
            var auth = this.Auth;
            var currentUser = auth.currentUser;
            var pendingCred = auth_1.GoogleAuthProvider.credentialFromResult(googleCredential);
            return (0, auth_1.linkWithCredential)(currentUser, pendingCred)
                .then(() => resolve())
                .catch(linkError => {
                if (linkError.code === 'auth/multi-factor-auth-required') {
                    // This is a known defect with this call: https://github.com/firebase/firebase-js-sdk/issues/9467
                    resolve();
                }
                else
                    reject(linkError);
            });
        });
    }
    async SignOut(suppressBiometrics = true) {
        react_native_1.DeviceEventEmitter.emit('event.signOut');
        if (suppressBiometrics) {
            this.SuppressBiometricAutoPromptOnce();
        }
        Chat_1.default.unload();
        await async_storage_1.default.removeItem('selectedClientId');
        await async_storage_1.default.removeItem('selectedProspectId');
        return (0, auth_1.signOut)(this.Auth);
    }
    Reauthenticate(password) {
        return (0, auth_1.reauthenticateWithCredential)(this.Auth.currentUser, auth_1.EmailAuthProvider.credential(this.Auth.currentUser.email?.toLowerCase(), password));
    }
    SendPasswordReset(email) {
        return (0, auth_1.sendPasswordResetEmail)(this.Auth, email);
    }
    ConfirmPasswordReset(code, password) {
        return (0, auth_1.confirmPasswordReset)(this.Auth, code, password);
    }
    UpdatePassword(password) {
        return (0, auth_1.updatePassword)(this.Auth.currentUser, password);
    }
    GetActionCodeInfo(code) {
        return (0, auth_1.checkActionCode)(this.Auth, code);
    }
    ApplyActionCode(code) {
        return (0, auth_1.applyActionCode)(this.Auth, code);
    }
    MfaGetStatus() {
        if (this.Auth?.currentUser == null)
            return null;
        const mfa = (0, auth_1.multiFactor)(this.Auth.currentUser);
        if (mfa?.enrolledFactors?.length > 0) {
            return mfa.enrolledFactors;
        }
        else {
            return null;
        }
    }
    MfaUnenroll(mfa) {
        Chat_1.default.unload();
        return (0, auth_1.multiFactor)(this.Auth.currentUser).unenroll(mfa);
    }
    MfaEnrollStart(phone, recaptcha) {
        return (0, auth_1.multiFactor)(this.Auth.currentUser).getSession().then((session) => {
            const options = { session, phoneNumber: phone };
            const provider = new auth_1.PhoneAuthProvider(this.Auth);
            return provider.verifyPhoneNumber(options, recaptcha);
        });
    }
    MfaTotpEnrollStart() {
        return (0, auth_1.multiFactor)(this.Auth.currentUser).getSession().then(async (session) => {
            let secret = await auth_1.TotpMultiFactorGenerator.generateSecret(session);
            let qrRegister = secret.generateQrCodeUrl(this.Auth.currentUser.email?.toLowerCase(), "Elevate by Nextep");
            return {
                secret,
                qrRegister
            };
        });
    }
    MfaEnrollFinish(id, code) {
        const credential = auth_1.PhoneAuthProvider.credential(id, code);
        const assertion = auth_1.PhoneMultiFactorGenerator.assertion(credential);
        return (0, auth_1.multiFactor)(this.Auth.currentUser).enroll(assertion);
    }
    MfaTotpEnrollFinish(secret, code) {
        let assertion = auth_1.TotpMultiFactorGenerator.assertionForEnrollment(secret, code);
        return (0, auth_1.multiFactor)(this.Auth.currentUser).enroll(assertion, "Auth App");
    }
    MfaVerifyStart(error) {
        const resolver = (0, auth_1.getMultiFactorResolver)(this.Auth, error);
        if (resolver?.hints?.length > 0) {
            return resolver;
        }
        else {
            return null;
        }
    }
    MfaVerifySendCode(resolver, index, recaptcha) {
        const options = {
            multiFactorUid: resolver.hints[index].uid,
            session: resolver.session
        };
        const provider = new auth_1.PhoneAuthProvider(this.Auth);
        return provider.verifyPhoneNumber(options, recaptcha);
    }
    MfaVerifyResolveCode(resolver, id, code) {
        const credential = auth_1.PhoneAuthProvider.credential(id, code);
        const assertion = auth_1.PhoneMultiFactorGenerator.assertion(credential);
        return resolver.resolveSignIn(assertion);
    }
    MfaVerifyTotpResolveCode(resolver, id, code) {
        const assertion = auth_1.TotpMultiFactorGenerator.assertionForSignIn(id, code);
        return resolver.resolveSignIn(assertion);
    }
    SuppressBiometricAutoPromptOnce() {
        this.suppressBiometricAutoPromptOnce = true;
    }
    ConsumeSuppressBiometricAutoPromptOnce() {
        const v = this.suppressBiometricAutoPromptOnce;
        this.suppressBiometricAutoPromptOnce = false;
        return v;
    }
}
exports.Authentication = Authentication;
const Session = new Authentication();
exports.default = Session;
